import "./instrument";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
// import { Formik } from "formik";
import Login from "./screens/auth/Login";
import Signup from "./screens/auth/Signup";
import Dashboard from "./screens/Dashboard/Dashboard";
import ContractorProfile from "./screens/Dashboard/Contractors/ContractorProfile";
import OtpScreen from "./screens/auth/OtpScreen";
import Profession from "./screens/auth/Profession";
import UserDetails from "./screens/auth/UserDetails";
import UserAddress from "./screens/auth/UserAddress";
import Sitehome from "./screens/Project Dashboard/SiteHome";
import WelcomePage from "./screens/auth/Welcome";
import AddYourOrganization from "./screens/Dashboard/AddOrganization";
import OrganizationDetailsForm from "./screens/Dashboard/AddOrganizationDetails";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import OrganizationDetails from "screens/Dashboard/OrganizationOwner";
import TaskDetails from "./screens/Project Dashboard/Tasks/TaskFormDetails";
import { AlertProvider } from "components/UI_components/Alert";
import SubscriptionModal from "./screens/Dashboard/Subscription/SubscriptionModal";
import { ForgotPassword } from "screens/auth/ResetPasswordRequest";
import { VerifyOTP } from "screens/auth/VerifyOTPReset";
import { NewPassword } from "screens/auth/NewPassword";
import MobileRedirectNotice from "components/MobileRedirectNotice";
import NetworkStatus from "components/UI_components/NetworkStatus"; // Import the new NetworkStatus component
import SnagsChat from "./screens/Project Dashboard/Snags/ChatWindow";
import OrganizationTeam from "./screens/Dashboard/Organization Team/OrganizationTeam";
import IndividualAccess from "./screens/Dashboard/Organization Team/IndividualAccess";
import DocumentManager from "./screens/Dashboard/Document-manager/DocumentManager";
import { rtdb } from "utils/firebase";
import { useScreenTimeFirebase } from "utils/hooks/useScreenTimeFirebase";
import { useEffect, useState } from "react";
import { environment } from "utils/constant";
import NotificationPopup from "components/NotificationPopup";
import { NotificationProvider } from "utils/Notifications/NotificationContext";
import Subscription from "screens/Dashboard/Subscription/Subscription";
import QuotationForm from "screens/Quotations";
import { getFinanceCategories } from "utils/getFinanceCategories";
import { Toaster } from "sonner";

const getStoredSessionState = () => {
  let user = null;
  let organization = null;

  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    user = null;
  }

  try {
    organization = JSON.parse(localStorage.getItem("organization") || "null");
  } catch (e) {
    organization = null;
  }

  return {
    userId: user?.user ?? null,
    organization: organization?.id ? organization : null,
  };
};

const queryClient = new QueryClient();

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("access");
  const isOwner = localStorage.getItem("isOwner") === "true";
  const location = useLocation();

  // Routes restricted to owners only
  const ownerOnlyRoutes = ["/dashboard", "/dashboard/home"];

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block non-owners from owner-only routes
  const normalizedPath =
    location.pathname.endsWith("/") && location.pathname.length > 1
      ? location.pathname.slice(0, -1)
      : location.pathname;

  if (!isOwner && ownerOnlyRoutes.includes(normalizedPath)) {
    return <Navigate to="/dashboard/projects" replace />;
  }

  return children;
};

function App() {
  const [sessionState, setSessionState] = useState(getStoredSessionState);
  const { userId, organization } = sessionState;

  useEffect(() => {
    const syncSessionState = () => {
      const nextState = getStoredSessionState();
      setSessionState((prevState) => {
        if (
          prevState.userId === nextState.userId &&
          prevState.organization?.id === nextState.organization?.id
        ) {
          return prevState;
        }
        return nextState;
      });
    };

    syncSessionState();

    window.addEventListener("storage", syncSessionState);
    window.addEventListener("organizationChanged", syncSessionState);
    window.addEventListener("authChanged", syncSessionState);
    window.addEventListener("focus", syncSessionState);

    const sessionSyncInterval = window.setInterval(syncSessionState, 1000);

    return () => {
      window.removeEventListener("storage", syncSessionState);
      window.removeEventListener("organizationChanged", syncSessionState);
      window.removeEventListener("authChanged", syncSessionState);
      window.removeEventListener("focus", syncSessionState);
      window.clearInterval(sessionSyncInterval);
    };
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem("access");
    if (!accessToken) return;
    getFinanceCategories();
  }, []);

  const { active, todayMs, flush, prepareForLogout } = useScreenTimeFirebase({
    rtdb,
    userId,
    pathBase: `screenTime-${environment}`,
    idleTimeoutMs: 60_000,
    flushIntervalMs: 60_000,
  });

  return (
    <>
      <MobileRedirectNotice />
      <NetworkStatus /> {/* Add the NetworkStatus component here */}
      <Toaster position="top-right" richColors />
      <NotificationProvider organizationId={organization?.id} userId={userId}>
        <NotificationPopup />
        <AlertProvider>
          <QueryClientProvider client={queryClient}>
            <Router>
              <Routes>
                <Route exact path="/subtask" element={<TaskDetails />} />
                <Route exact path="/login" element={<Login />} />
                <Route exact path="/signup" element={<Signup />} />
                <Route exact path="/otp-screen" element={<OtpScreen />} />
                <Route
                  exact
                  path="/quotations/:token"
                  element={<QuotationForm />}
                />
                <Route
                  exact
                  path="/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route exact path="/verify-otp" element={<VerifyOTP />} />
                <Route exact path="/new-password" element={<NewPassword />} />
                <Route
                  exact
                  path="/"
                  element={<Navigate to="/login" replace />}
                />
                <Route
                  exact
                  path="/subscription"
                  element={<Subscription prepareForLogout={prepareForLogout} />}
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard
                        active={active}
                        flush={flush}
                        todayMs={todayMs}
                        prepareForLogout={prepareForLogout}
                      />
                    </ProtectedRoute>
                  }
                >
                  {/* Nested routes for dashboard tabs */}
                  <Route
                    index
                    element={<Navigate to="/dashboard/home" replace />}
                  />
                  <Route path="home" element={null} />
                  <Route path="projects" element={null} />
                  <Route path="procurement" element={null} />
                  <Route path="warehouse" element={null} />
                  <Route
                    path="central-inventory"
                    element={<Navigate to="/dashboard/procurement" replace />}
                  />
                  <Route path="vendor" element={null} />
                  <Route path="contractor" element={null} />
                  <Route
                    path="contractor/:id"
                    element={<ContractorProfile />}
                  />
                  <Route path="finance" element={null} />
                  <Route path="organization-team" element={null} />
                  <Route
                    path="document-manager"
                    element={<DocumentManager />}
                  />
                  <Route path="quotation-generator" element={null} />
                  <Route path="lead-management" element={null} />
                  <Route path="design" element={null} />
                  <Route path="petty-cash" element={null} />
                  <Route path="snags" element={null} />
                  <Route
                    path="global-communication/:snagRoomId?"
                    element={null}
                  />
                  <Route path="global-media" element={null} />
                  <Route path="global-report" element={null} />
                  <Route path="organization" element={null} />
                  <Route path="help" element={null} />
                  <Route path="settings" element={null} />
                  <Route path="raise-request" element={null} />
                </Route>
                <Route exact path="/profession" element={<Profession />} />
                <Route path="/userdetails" element={<UserDetails />} />
                <Route path="/useraddress" element={<UserAddress />} />
                {/* <Route path="/sitehome" element={<Sitehome />} /> */}
                <Route
                  path="/site/:siteId/*"
                  element={
                    <ProtectedRoute>
                      <Sitehome prepareForLogout={prepareForLogout} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route
                  exact
                  path="/subscription"
                  element={
                    <SubscriptionModal
                      onClose={() => {
                        // console.log("close");
                      }}
                      isOpen={true}
                    />
                  }
                />
                <Route
                  path="/add-organization"
                  element={<AddYourOrganization />}
                />
                <Route
                  exact
                  path="/organization-details"
                  element={<OrganizationDetailsForm />}
                />
                {/* <Route
                exact
                path="/chat/:id"
                element={
                  <ProtectedRoute>
                    <SnagsChat />
                  </ProtectedRoute>
                }
              /> */}
                <Route
                  exact
                  path="/organization/team"
                  element={
                    <ProtectedRoute>
                      <OrganizationTeam />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/organization/team/:memberId"
                  element={
                    <ProtectedRoute>
                      <IndividualAccess />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </QueryClientProvider>
        </AlertProvider>
      </NotificationProvider>
    </>
  );
}

export default App;
